import { config, endpoints } from "../utils/config";

import axios from "axios";
// Create axios instance with base configuration
const api = axios.create({
  baseURL: config.api.baseURL,
  timeout: config.api.timeout,
  headers: {
    "Content-Type": "application/json",
    "If-Match": "*",
  },
  withCredentials: true,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("gelato_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log API calls in development
    if (process.env.NODE_ENV === "development") {
      console.log(`🔄 API Call: ${config.method?.toUpperCase()} ${config.url}`);
      if (token) {
        console.log(`🔑 Token: ${token.substring(0, 20)}...`);
      }
    }

    return config;
  },
  (error) => {
    console.error("❌ Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === "development") {
      console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  async (error) => {
    console.error("❌ API Error:", error);

    // Enhanced error logging
    if (error.response) {
      console.error("Response Error:", {
        status: error.response.status,
        data: error.response.data,
        url: error.config?.url,
        method: error.config?.method,
      });
    } else if (error.request) {
      console.error("Network Error:", error.message);
    }

    // Don't try to refresh demo tokens
    const token = localStorage.getItem("gelato_token");
    if (token && token.startsWith("demo-token")) {
      console.log("🔧 Demo mode: Skipping token refresh");

      // For demo mode, mock responses for specific endpoints
      if (error.response?.status === 401 || error.response?.status === 404) {
        console.warn("Demo mode: API not available, using mock response");

        // Return mock response based on the endpoint
        if (error.config.url.includes("/master-files/upload")) {
          return Promise.reject({
            response: {
              status: 401,
              data: {
                success: false,
                message:
                  "Demo mode: Backend API not available. Using client-side processing.",
              },
            },
          });
        } else if (error.config.url.includes("/master-files")) {
          return Promise.resolve({
            data: {
              success: true,
              data: [],
            },
          });
        } else if (error.config.url.includes("/master-data")) {
          return Promise.resolve({
            data: {
              success: true,
              data: [],
            },
          });
        }
      }

      return Promise.reject(error);
    }

    // Handle 401 unauthorized errors for real tokens
    if (error.response?.status === 401 && !error.config._retry) {
      error.config._retry = true;

      try {
        const refreshToken = localStorage.getItem("gelato_refresh_token");
        if (
          refreshToken &&
          process.env.REACT_APP_ENABLE_REFRESH_TOKEN === "true"
        ) {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/auth/refresh`,
            { refreshToken }
          );

          const { token: newToken } = response.data.data;
          localStorage.setItem("gelato_token", newToken);

          error.config.headers.Authorization = `Bearer ${newToken}`;
          return api(error.config);
        }
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        localStorage.removeItem("gelato_token");
        localStorage.removeItem("gelato_refresh_token");
        localStorage.removeItem("gelato_user");
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post(endpoints.auth.login, credentials),
  logout: () => api.post(endpoints.auth.logout),
  getProfile: () => api.get(endpoints.auth.profile),
  register: (userData) => api.post(endpoints.auth.register, userData),
};

// Users API
export const usersAPI = {
  getAll: () => api.get(endpoints.users),
  create: (userData) => api.post(endpoints.users, userData),
  update: (id, userData) => api.put(`${endpoints.users}/${id}`, userData),
  delete: (id) => api.delete(`${endpoints.users}/${id}`),
  toggleStatus: (id) => api.patch(`${endpoints.users}/${id}/toggle-status`),
};

// Enhanced master files API with better error handling
export const masterFilesAPI = {
  upload: (formData) => {
    console.log("📤 Uploading master files...");

    // Check file sizes before upload
    const files = [];
    for (let [key, value] of formData.entries()) {
      if (value instanceof File) {
        files.push({ name: value.name, size: value.size });
      }
    }

    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    const maxSize = 50 * 1024 * 1024; // 50MB

    console.log(
      `📊 Total file size: ${(totalSize / 1024 / 1024).toFixed(2)} MB`
    );

    if (totalSize > maxSize) {
      return Promise.reject(
        new Error(
          `Files too large: ${(totalSize / 1024 / 1024).toFixed(
            2
          )} MB (max: 50 MB)`
        )
      );
    }

    return api
      .post("/master-files/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 300000, // 5 minutes for file uploads
        maxContentLength: 100 * 1024 * 1024, // 100MB max content length
        maxBodyLength: 100 * 1024 * 1024, // 100MB max body length
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            console.log(
              `📊 Upload progress: ${progress}% (${(
                progressEvent.loaded /
                1024 /
                1024
              ).toFixed(2)} MB / ${(progressEvent.total / 1024 / 1024).toFixed(
                2
              )} MB)`
            );
          }
        },
      })
      .catch((error) => {
        console.error("📤 Upload failed:", error);

        // Provide more specific error messages
        if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
          throw new Error(
            "Upload timeout - file processing took too long. Please try with smaller files or check your connection."
          );
        } else if (error.response?.status === 413) {
          throw new Error("File too large - maximum size is 50MB per file");
        } else if (error.response?.status === 401) {
          throw new Error(
            "Authentication failed - please refresh the page and try again"
          );
        } else if (error.response?.status === 500) {
          throw new Error(
            "Server error during file processing - please check file format and try again"
          );
        } else if (!error.response) {
          throw new Error(
            "Network error - please check your internet connection and try again"
          );
        } else if (error.message.includes("timeout")) {
          throw new Error("Upload timeout - please try with smaller files");
        }

        throw error;
      });
  },

  getFiles: () => {
    return api.get("/master-files").catch((error) => {
      console.warn("Failed to load files from server, returning empty array");
      return { data: { success: true, data: [] } };
    });
  },

  getMasterData: (params = {}) => {
    console.log("🔍 Loading master data from database...", params);
    return api
      .get("/master-files/data", { params })
      .then((response) => {
        console.log("📊 Master data API response:", response.data);
        if (response.data.pagination) {
          console.log("📄 Pagination info:", response.data.pagination);
        }
        return response;
      })
      .catch((error) => {
        console.error("❌ Failed to load master data from server:", error);

        // Try debug endpoint for troubleshooting
        if (process.env.NODE_ENV === "development") {
          api
            .get("/master-files/debug")
            .then((debugResponse) => {
              console.log("🔍 Debug info:", debugResponse.data);
            })
            .catch((debugError) => {
              console.log("🔍 Debug endpoint not available");
            });
        }

        // Return empty array instead of throwing error to prevent UI crash
        return {
          data: {
            success: true,
            data: [],
            pagination: {
              currentPage: 1,
              totalPages: 0,
              totalRecords: 0,
              limit: 100,
              offset: 0,
              hasNextPage: false,
              hasPrevPage: false,
            },
            message: "No data available from database",
          },
        };
      });
  },

  // Add method to get data count with search
  getDataCount: (search = "") => {
    return api
      .get("/master-files/data/count", {
        params: search ? { search } : {},
      })
      .catch((error) => {
        console.warn("Failed to get data count from server");
        return { data: { success: true, count: 0 } };
      });
  },

  // Add debug method
  getDebugInfo: () => {
    return api.get("/master-files/debug").catch((error) => {
      console.warn("Debug endpoint not available");
      return { data: { success: false, debug_info: null } };
    });
  },

  updateMasterData: (id, data) => api.put(`/master-data/${id}`, data),

  deleteMasterData: (id) => {
    return api.delete(`/master-files/${id}`).catch((error) => {
      console.warn("Failed to delete from server, continuing anyway");
      return {
        data: {
          success: true,
          message: "Delete simulated in demo mode",
        },
      };
    });
  },
};

export const jobsAPI = {
  getAll: (params = {}) => api.get("/jobs", { params }),
  getById: (id) => api.get(`/jobs/${id}`),
  create: (jobData) => {
    console.log("📝 Creating new job...", jobData);
    return api.post("/jobs", jobData);
  },
  update: (id, jobData) => api.put(`/jobs/${id}`, jobData),
  delete: (id) => {
    console.log(`🗑️ Deleting job with ID: ${id}`);
    return api.delete(`/jobs/${id}`);
  },

  // Enhanced file upload for jobs
  uploadFiles: (jobId, formData) => {
    console.log(`📤 Uploading files to job ${jobId}...`);
    return api.post(`/jobs/${jobId}/files`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 300000, // 5 minutes
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          console.log(`📊 Job files upload progress: ${progress}%`);
        }
      },
    });
  },

  // Store processed master data for a job
  storeMasterData: (jobId, masterData) => {
    console.log(`💾 Storing master data for job ${jobId}...`);
    return api.post(
      `/jobs/${jobId}/master-data`,
      { masterData },
      {
        timeout: 120000, // 2 minutes for large datasets
      }
    );
  },

  // Get merged master data for a job (real data, not mock)
  getMasterData: (jobId, params = {}) => {
    console.log(`📊 Loading master data for job ${jobId}...`);
    return api.get(`/jobs/${jobId}/master`, { params }).catch((error) => {
      console.warn(
        `Could not load master data for job ${jobId}:`,
        error.message
      );
      return {
        data: {
          success: false,
          data: [],
          message: "No master data available for this job",
        },
      };
    });
  },

  // Merge uploaded files with existing master data
  mergeWithMaster: (jobId) => {
    console.log(`🔄 Merging job ${jobId} files with master data...`);
    return api.post(
      `/jobs/${jobId}/merge`,
      {},
      {
        timeout: 300000, // 5 minutes for processing
      }
    );
  },

  // Legacy AR04 upload (for backward compatibility)
  uploadAR04: (id, formData) =>
    api.post(`/jobs/${id}/ar04`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  runComparison: (id) => api.post(`/jobs/${id}/compare`),
  getComparisons: (id, params = {}) =>
    api.get(`/jobs/${id}/comparisons`, { params }),

  // Store comparison results for a job
  storeComparisonResults: (jobId, comparisonData) => {
    console.log(`💾 Storing comparison results for job ${jobId}...`);
    return api.post(
      `/jobs/${jobId}/comparison-results`,
      { comparisonData },
      {
        timeout: 120000, // 2 minutes for large datasets
      }
    );
  },

  // Get comparison results for a job
  getComparisonResults: (jobId, params = {}) => {
    console.log(`⚖️ Loading comparison results for job ${jobId}...`);
    return api
      .get(`/jobs/${jobId}/comparison-results`, { params })
      .catch((error) => {
        console.warn(
          `Could not load comparison results for job ${jobId}:`,
          error.message
        );
        return {
          data: {
            success: false,
            data: [],
            message: "No comparison results available for this job",
          },
        };
      });
  },

  // Legacy method for backward compatibility
  getResults: (id, params = {}) => api.get(`/jobs/${id}/results`, { params }),

  exportResults: (id, format = "xlsx") =>
    api.get(`/jobs/${id}/export`, {
      params: { format },
      responseType: "blob",
    }),

  // Get job history
  getJobHistory: async (jobId) => {
    try {
      const response = await api.get(`/jobs/${jobId}/history`);
      return response;
    } catch (error) {
      console.error("Failed to get job history:", error);
      throw error;
    }
  },

  // Add job history entry
  addJobHistory: async (jobId, historyData) => {
    try {
      const response = await api.post(`/jobs/${jobId}/history`, historyData);
      return response;
    } catch (error) {
      console.error("Failed to add job history:", error);
      throw error;
    }
  },

  // Get historical error data for a specific history entry
  getHistoryErrorData: async (jobId, historyId) => {
    try {
      const response = await api.get(
        `/jobs/${jobId}/history/${historyId}/errors`
      );
      return response;
    } catch (error) {
      console.error("Failed to get historical error data:", error);
      throw error;
    }
  },

  // Add job history entry with error data
  addJobHistoryWithErrorData: async (jobId, historyData) => {
    try {
      console.log(
        `📝 Adding enhanced job history with error data for job ${jobId}...`
      );
      console.log(
        `   - Previous error data: ${
          historyData.previous_error_data?.length || 0
        } records`
      );

      const response = await api.post(
        `/jobs/${jobId}/history-with-errors`,
        historyData
      );

      if (response.data.success) {
        console.log(
          `✅ Enhanced history entry created with ${
            response.data.data.previous_error_records_stored || 0
          } error records stored`
        );
        return response;
      } else {
        console.warn(
          `⚠️ Failed to add enhanced job history:`,
          response.data.message
        );
        return response;
      }
    } catch (error) {
      console.error(`❌ Enhanced job history API error:`, error);
      throw error;
    }
  },
};

export const logsAPI = {
  getAll: (params = {}) => api.get("/logs", { params }),
  export: (params = {}) =>
    api.get("/logs/export", {
      params,
      responseType: "blob",
    }),
};

export const permissionsAPI = {
  getAll: () => api.get("/permissions"),
  getRoles: () => api.get("/roles"),
  createRole: (roleData) => api.post("/roles", roleData),
  updateRole: (id, roleData) => api.put(`/roles/${id}`, roleData),
  deleteRole: (id) => api.delete(`/roles/${id}`),
};

// Dashboard API using existing axios instance
export const dashboardAPI = {
  getStats: () => {
    return api.get("/dashboard/stats").catch((error) => {
      console.warn("Failed to fetch dashboard stats, using mock data");
      return {
        data: {
          success: true,
          data: {
            total_jobs: 12,
            active_jobs: 8,
            total_comparisons: 1245,
            success_rate: "94.5%",
            total_master_records: 25000,
          },
        },
      };
    });
  },

  getRecentComparisons: (days = 7) => {
    return api
      .get(`/dashboard/recent-comparisons?days=${days}`)
      .catch((error) => {
        console.warn("Failed to fetch recent comparisons, using mock data");
        return {
          data: {
            success: true,
            data: [
              { date: "2024-01-01", comparison_count: 45 },
              { date: "2024-01-02", comparison_count: 52 },
              { date: "2024-01-03", comparison_count: 38 },
              { date: "2024-01-04", comparison_count: 65 },
              { date: "2024-01-05", comparison_count: 59 },
              { date: "2024-01-06", comparison_count: 42 },
              { date: "2024-01-07", comparison_count: 35 },
            ],
          },
        };
      });
  },

  getComparisonStatus: () => {
    return api.get("/dashboard/comparison-status").catch((error) => {
      console.warn("Failed to fetch comparison status, using mock data");
      return {
        data: {
          success: true,
          data: [
            { match_status: "MATCHED", count: 1050 },
            { match_status: "PARTIAL", count: 125 },
            { match_status: "MISMATCH", count: 45 },
            { match_status: "NOT_FOUND", count: 25 },
          ],
        },
      };
    });
  },

  getRecentActivities: (limit = 10) => {
    return api.get(`/dashboard/activities?limit=${limit}`).catch((error) => {
      console.warn("Failed to fetch recent activities, using mock data");
      return {
        data: {
          success: true,
          data: [
            {
              type: "COMPARISON_SUCCESS",
              description: "เปรียบเทียบ AR04 สำเร็จ",
              created_at: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
            },
            {
              type: "FILE_UPLOAD",
              description: "อัปโหลด Master Files ใหม่",
              created_at: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
            },
            {
              type: "JOB_CREATED",
              description: "สร้างงานใหม่: Q1 2024 Report",
              created_at: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
            },
          ],
        },
      };
    });
  },
};

export default api;
